import { useForm } from '@tanstack/react-form'
import { getRouteApi, useNavigate } from '@tanstack/react-router'
import { Toast, handleFormSubmissionError } from 'ui/components'
import { ManageDocumentPage } from '~bank-documents/routes/commonPages/ManageDocumentPage'
import { useSaveDocument } from '~bank-documents/service/saveDocument'
import { DocumentEditWebPageResponse, ManageDocumentFormType } from '~bank-documents/types/documentEdit'
import { mapManageDocumentRequest } from '~bank-documents/utils/mapManageDocumentRequest'

import { useDeleteDocumentConfirmation } from '../../hooks/useDeleteDocumentConfirmation'
import { bankDocumentMessages as m } from '../../messages/bankDocumentMessages'

const routeApi = getRouteApi('/_app/documents/$documentId/edit')

const EditDocument = () => {
  const navigate = useNavigate()

  const documentId = routeApi.useParams({ select: (s) => s.documentId })
  const { returnUrl } = routeApi.useSearch()

  const webPageData: DocumentEditWebPageResponse = routeApi.useLoaderData()

  const { form, ...appearanceInfo } = webPageData

  const formApi = useForm({
    defaultValues: new ManageDocumentFormType(false, form),
    onSubmit: async ({ value }) => await saveDocument({ id: Number(documentId), ...mapManageDocumentRequest(value) })
  })

  const { routine: saveDocument } = useSaveDocument({
    onSuccess: async () => {
      if (returnUrl) {
        await navigate({ to: returnUrl as never })
      } else {
        await navigate({ to: '/documents/$documentId', params: { documentId } })
      }
      Toast.successSmall('The document was successfully updated.')
    },
    onError: (error) => {
      handleFormSubmissionError<ManageDocumentFormType>(error, formApi)
    }
  })

  const documentName = form.name

  const { openDeleteDocumentConfirmation, DeleteConfirmation } = useDeleteDocumentConfirmation({
    onSuccess: () => navigate({ to: '/documents' })
  })

  return (
    <>
      <DeleteConfirmation />
      <ManageDocumentPage
        form={formApi}
        appearanceInfo={appearanceInfo}
        isNew={false}
        panelTitle={m['bankDocuments.editForm.title']}
        pageSubtitle={documentName}
        onDocumentDelete={() => openDeleteDocumentConfirmation({ id: Number(documentId), name: documentName })}
      />
    </>
  )
}

export default EditDocument
